import {ChangeEvent, FC, useCallback, useMemo, useState} from 'react';
import {MediaUploadModal} from '~/common/components/MediaUploadModal/MediaUploadModal';
import {UYO_STICKER_UPLOAD_PREFIX} from '~/common/FabricInvitation/EventCreateDesign/components/Sticker/components/Stickers/constants';
import {useCachedMedia} from '~/common/hooks/useCachedMedia';
import {STICKER_UPLOAD_INDEXED_DB_STORE_NAME, StoredData} from '~/common/indexed-db/dexieStores';
import {StickerUploadIcon} from './StickerUploadIcon';
import './StickerUpload.sass';

const MAX_UPLOAD_SIZE_LIMIT = 1024 * 5; // 5MB
const ACCEPTED_FILE_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/heic',
  'image/gif',
];
interface StickerListProps {
  onClickHandler: (icon: StoredData, id: string) => void;
}

// Lazy load heic2any
const importHeic2any = () => import('heic2any').then((module) => module.default);

type ErrorKind = 'none' | 'size' | 'type';

export const StickerUpload: FC<StickerListProps> = ({onClickHandler}) => {
  const {storeAndUpdateCachedMediaById} = useCachedMedia(STICKER_UPLOAD_INDEXED_DB_STORE_NAME);
  const [error, setError] = useState<ErrorKind>('none');

  const onImageChange = useCallback(
    async (fileEvent: ChangeEvent<HTMLInputElement>) => {
      if (!fileEvent?.target?.files) return;

      let currentFile = fileEvent.target?.files[0];
      if (!currentFile) return;

      if (!ACCEPTED_FILE_MIME_TYPES.includes(currentFile.type)) {
        setError('type');
        return;
      }

      const reader = new FileReader();
      if (Math.round(currentFile.size / 1024) >= MAX_UPLOAD_SIZE_LIMIT) {
        setError('size');

        // this enables the same file to be revalidated if uploaded
        fileEvent.target.value = '';
        return;
      }

      if (currentFile.type === 'image/heic') {
        // fabric doesn't allow heic file types so...
        // convert heic to jpeg
        try {
          // Dynamically import heic2any only when needed
          const heic2any = await importHeic2any();
          const blob = await heic2any({blob: currentFile, toType: 'image/jpeg', quality: 0.9});
          currentFile = new File([blob] as unknown as BlobPart[], 'image.jpeg', {
            type: 'image/jpeg',
          });
        } catch (error) {
          evite.error('Conversion failed', error);
          return;
        }
      }

      reader.addEventListener('load', async () => {
        const result = reader.result as string;
        const id = `${UYO_STICKER_UPLOAD_PREFIX}${crypto.randomUUID()}`;
        const newMedia = await storeAndUpdateCachedMediaById(result, id);

        // this enables the same file to be revalidate if uploaded
        fileEvent.target.value = '';
        if (newMedia) {
          onClickHandler(newMedia, newMedia.id);
        }
      });

      reader.onerror = (evt: ProgressEvent) => {
        evite.error('file onerror', evt);
      };

      reader.readAsDataURL(currentFile);
    },
    [onClickHandler, storeAndUpdateCachedMediaById]
  );

  const mediaUploadModals = useMemo(() => {
    return (
      <>
        {error === 'size' && (
          <MediaUploadModal
            hideModal={() => setError('none')}
            header="Your file could not be uploaded."
            description="Looks like your file is too large. Make sure your file is less than 5MB, then try again."
            ctaButtonText="Got it"
          />
        )}
        {error === 'type' && (
          <MediaUploadModal
            hideModal={() => setError('none')}
            header="Your file could not be uploaded."
            description="Looks like we don't accept that file type. Please try again with a PNG, JPEG, or HEIC."
            ctaButtonText="Got it"
          />
        )}
      </>
    );
  }, [error]);

  return useMemo(
    () => (
      <>
        {mediaUploadModals}
        <label htmlFor="sticker-upload" className="sticker-upload-btn">
          <StickerUploadIcon />
        </label>
        <input
          type="file"
          id="sticker-upload"
          name="sticker-upload"
          accept={ACCEPTED_FILE_MIME_TYPES.join(',')}
          style={{display: 'none'}}
          onChange={(fileEvent) => onImageChange(fileEvent)}
          data-qa-id="sticker-upload-btn"
        />
      </>
    ),
    [onImageChange, mediaUploadModals]
  );
};
